import React from "react";
import BaseModal from "react-modal";
import type { Props as BaseModalProps } from "react-modal";
import { Button } from "../Button/Button";
import { RemoveScroll } from "react-remove-scroll";
import clsx from "clsx";
import styles from "./Modal.module.scss";
import { Label } from "../types/labels";
import { isPresent } from "../utils/checks";

export interface ModalProps {
  modal: BaseModalProps;
  title?: string;
  size?: "small" | "big" | "medium" | "fullscreen";
  dataContentName: string;
  "data-content-piece"?: string;
  className?: string;
  labels?: {
    close: Label;
  };
}

export interface ModalBodyProps {
  className?: string;
  variant?: "boxed" | "wide" | "full";
  paddingBottom?: boolean;
  paddingTop?: boolean;
  removeScroll?: boolean;
}

export const ModalBody: React.FC<React.PropsWithChildren<ModalBodyProps>> = ({
  className,
  variant = "wide",
  paddingBottom = true,
  paddingTop = true,
  removeScroll = true,
  children
}) => {
  return (
    <RemoveScroll
      enabled={removeScroll}
      className={clsx(className, {
        [styles.body]: true,
        [styles.bodyWide]: variant == "wide",
        [styles.bodyBoxed]: variant == "boxed",
        [styles.bodyFull]: variant == "full",
        [styles.withPaddingTop]: paddingTop,
        [styles.withPaddingBottom]: paddingBottom
      })}
    >
      {children}
    </RemoveScroll>
  );
};

export const ModalBodyContent: React.FC<
  React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
> = ({ children, className, ...props }) => {
  return (
    <div {...props} className={clsx(styles.bodyContent, className)}>
      {children}
    </div>
  );
};

export const ModalBodyTitle: React.FC<React.PropsWithChildren<unknown>> = ({
  children
}) => {
  return <div className={styles.bodyTitle}>{children}</div>;
};

type ModalFooterProps = {
  className?: string;
};
export const ModalFooter: React.FC<
  React.PropsWithChildren<ModalFooterProps>
> = ({ children, className }) => {
  return (
    <footer className={clsx(styles.modalFooter, className)}>{children}</footer>
  );
};

export const modalSetAppElement = (id: string): void =>
  BaseModal.setAppElement(id);

export const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  children,
  dataContentName,
  modal,
  title,
  size = "big",
  className,
  labels = {
    close: "Close"
  },
  ...props
}) => {
  return (
    <BaseModal
      {...modal}
      data={{ "content-name": dataContentName || props["data-content-name"] }}
      contentLabel="modal"
      closeTimeoutMS={300}
      overlayClassName={{
        base: styles.overlay,
        afterOpen: styles.overlayAfterOpen,
        beforeClose: styles.overlayBeforeClose
      }}
      className={clsx(
        styles.content,
        {
          [styles.contentBig]: size == "big",
          [styles.contentMedium]: size == "medium",
          [styles.contentSmall]: size == "small",
          [styles.contentFullscreen]: size == "fullscreen"
        },
        className
      )}
    >
      {(isPresent(title) || isPresent(modal.onRequestClose)) && (
        <header className={styles.header}>
          <div className={styles.title}>{title}</div>
          {modal.onRequestClose && (
            <Button
              renderAs="button"
              variant="text"
              size="regular"
              iconName="close"
              dataContentPiece="close-modal"
              onClick={modal.onRequestClose}
            >
              {labels.close}
            </Button>
          )}
        </header>
      )}
      {children}
    </BaseModal>
  );
};

export type { BaseModalProps };
